import { useEffect, useRef } from 'react'
import { consumer } from '@/channels/consumer'
import type { Subscription } from '@rails/actioncable'

export interface NewCallEvent {
  type: 'new_call'
  telephony_session_id: string
  start_time: string
  duration: number
  from_phone: string | null
  from_name: string | null
  to_phone: string | null
  to_name: string | null
  direction: string
  result: string
  has_recording: boolean
  timestamp: string
}

interface UseCallsChannelOptions {
  onNewCall?: (event: NewCallEvent) => void
}

/**
 * Hook to subscribe to the CallsChannel for real-time call updates
 */
export function useCallsChannel({ onNewCall }: UseCallsChannelOptions = {}) {
  const subscriptionRef = useRef<Subscription | null>(null)
  const onNewCallRef = useRef(onNewCall)

  // Keep the callback ref updated
  useEffect(() => {
    onNewCallRef.current = onNewCall
  }, [onNewCall])

  // Subscribe to the channel
  useEffect(() => {
    console.log('[CallsChannel] Subscribing...')

    subscriptionRef.current = consumer.subscriptions.create('CallsChannel', {
      connected() {
        console.log('[CallsChannel] Connected')
      },

      disconnected() {
        console.log('[CallsChannel] Disconnected')
      },

      received(data: NewCallEvent) {
        console.log('[CallsChannel] Received:', data)

        if (data.type === 'new_call' && onNewCallRef.current) {
          onNewCallRef.current(data)
        }
      },
    })

    // Cleanup on unmount
    return () => {
      console.log('[CallsChannel] Unsubscribing...')
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }
  }, [])

  return {
    isConnected: !!subscriptionRef.current,
  }
}

export default useCallsChannel
